import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getLastAvailableMarketDate,
  getMarketDataBySelectedDate,
} from 'common/src/api/market_data';

/**
 * Custom hook que centraliza la lógica de sincronización de fechas
 * para evitar competing useEffects y re-fetches duplicados
 *
 * Casos que maneja:
 * 1. Cambio de species → busca última fecha disponible
 * 2. Cambio de season → busca última fecha para esa temporada
 * 3. Page reload → considera species + season
 * 4. Demo mode → usa fechas fijas
 * 5. Cache → mantiene últimas fechas por species
 */

interface UseMarketDateSyncProps {
  speciesId?: number;
  season?: string;
  hasDemo?: boolean;
  viewMode?: 'day' | 'week';
  demoDefaultDate?: string;
  daysBack?: number; // Para rangos: cuántos días atrás del último (default: 7 para day, 15 para week)
  origins?: string[]; // Orígenes seleccionados. Ancla el rango a la última fecha CON alguno de esos orígenes.
}

interface DateCache {
  [speciesId: number]: {
    date: string;
    startDate: string;
    endDate: string;
    season?: string;
    originsKey?: string;
  };
}

const useMarketDateSync = ({
  speciesId,
  season,
  hasDemo = false,
  viewMode = 'day',
  demoDefaultDate = '2024-12-20',
  daysBack,
  origins,
}: UseMarketDateSyncProps) => {

  // Clave estable para comparar/cachear: el array cambia de identidad en cada
  // render aunque tenga el mismo contenido.
  const originsKey = (origins ?? []).slice().sort().join(',');
  // Cache de fechas por species (persiste mientras el componente esté montado)
  const [dateCache, setDateCache] = useState<DateCache>({});

  // Estados de fechas
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const today = new Date();
    const past = new Date(today);
    past.setDate(today.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15)));
    return [past, today];
  });

  const [isLoading, setIsLoading] = useState(true);

  // Ref para evitar fetches duplicados
  const fetchingRef = useRef(false);
  const lastFetchKey = useRef<string>('');

  /**
   * Función principal que maneja la sincronización de fechas
   * Unifica toda la lógica para evitar competing useEffects
   */
  const syncDates = useCallback(async () => {
    // Validaciones iniciales
    if (speciesId === undefined) return;

    // Crear key único para esta combinación de parámetros
    const fetchKey = `${speciesId}-${season || 'no-season'}-${originsKey || 'all'}`;

    // Evitar fetch duplicado si ya está en proceso
    if (fetchingRef.current && lastFetchKey.current === fetchKey) {
      console.log('⏭️ Skipping duplicate fetch for:', fetchKey);
      return;
    }

    // Demo mode: usar fechas fijas
    if (hasDemo) {
      setSelectedDate(demoDefaultDate);
      const endDate = new Date(`${demoDefaultDate}T12:00:00.000Z`);
      const startDate = new Date(`${demoDefaultDate}T12:00:00.000Z`);
      startDate.setDate(startDate.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15)));
      setDateRange([startDate, endDate]);
      setIsLoading(false);
      return;
    }

    // Verificar si ya tenemos esta combinación en cache
    const cached = dateCache[speciesId];
    if (cached && cached.season === season && cached.originsKey === originsKey && season) {
      // Si la season coincide con el cache, usar datos cacheados
      console.log('✅ Using cached dates for species', speciesId, 'season', season);
      setSelectedDate(cached.date);
      const start = new Date(`${cached.startDate}T12:00:00.000Z`);
      const end = new Date(`${cached.endDate}T12:00:00.000Z`);
      setDateRange([start, end]);
      setIsLoading(false);
      return;
    }

    // Si tenemos cache pero sin season específica y no estamos filtrando por season
    if (cached && !season && !cached.season && cached.originsKey === originsKey) {
      console.log('✅ Using cached dates for species', speciesId, '(no season filter)');
      setSelectedDate(cached.date);
      const start = new Date(`${cached.startDate}T12:00:00.000Z`);
      const end = new Date(`${cached.endDate}T12:00:00.000Z`);
      setDateRange([start, end]);
      setIsLoading(false);
      return;
    }

    // Si llegamos aquí, necesitamos fetchear
    fetchingRef.current = true;
    lastFetchKey.current = fetchKey;
    setIsLoading(true);

    try {
      console.log('🔍 Fetching last available date for:', {
        speciesId,
        season: season || 'all',
      });

      // Verificar si hay datos para la fecha actual seleccionada
      if (selectedDate) {
        const dataForCurrentDate = await getMarketDataBySelectedDate(
          selectedDate,
          speciesId
        );

        // Si hay datos para la fecha actual y la season coincide (o no hay filtro)
        if (dataForCurrentDate && dataForCurrentDate.length > 0) {
          // Verificar que la data coincida con la season si está filtrada
          const matchesSeason = season
            ? dataForCurrentDate.some((item) => item.season === season)
            : true;

          if (matchesSeason) {
            console.log('✅ Current date has data, keeping it');

            // Actualizar cache
            const endDate = new Date(`${selectedDate}T12:00:00.000Z`);
            const startDate = new Date(`${selectedDate}T12:00:00.000Z`);
            startDate.setDate(
              startDate.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15))
            );

            setDateCache((prev) => ({
              ...prev,
              [speciesId]: {
                date: selectedDate,
                startDate: startDate.toISOString().split('T')[0],
                endDate: selectedDate,
                season,
                originsKey,
              },
            }));

            setIsLoading(false);
            fetchingRef.current = false;
            return;
          }
        }
      }

      // Buscar última fecha disponible
      let lastAvailableDate = await getLastAvailableMarketDate(
        speciesId,
        season,
        origins
      );

      // Si ese origen no tiene ninguna fecha para la especie (por ejemplo palta
      // y naranja no tienen data chilena), se vuelve a anclar sin filtrar por
      // origen. Preferimos un rango con datos de otro origen antes que caer al
      // fallback de "hoy", que deja el gráfico vacío.
      if (!lastAvailableDate && origins && origins.length > 0) {
        console.log('↩️ Sin fechas para', originsKey, '- reintentando sin filtro');
        lastAvailableDate = await getLastAvailableMarketDate(speciesId, season);
      }

      if (lastAvailableDate) {
        console.log('✅ Found last available date:', lastAvailableDate);

        const endDate = new Date(`${lastAvailableDate}T12:00:00.000Z`);
        const startDate = new Date(`${lastAvailableDate}T12:00:00.000Z`);
        startDate.setDate(
          startDate.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15))
        );

        const formattedStartDate = startDate.toISOString().split('T')[0];

        // Actualizar estados
        setSelectedDate(lastAvailableDate);
        setDateRange([startDate, endDate]);

        // Actualizar cache
        setDateCache((prev) => ({
          ...prev,
          [speciesId]: {
            date: lastAvailableDate,
            startDate: formattedStartDate,
            endDate: lastAvailableDate,
            season,
            originsKey,
          },
        }));
      } else {
        console.warn('⚠️ No data found for species', speciesId);

        // Usar fecha actual como fallback
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        setSelectedDate(todayString);

        const endDate = new Date(today);
        const startDate = new Date(today);
        startDate.setDate(
          startDate.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15))
        );
        setDateRange([startDate, endDate]);

        // Actualizar cache con fallback
        setDateCache((prev) => ({
          ...prev,
          [speciesId]: {
            date: todayString,
            startDate: startDate.toISOString().split('T')[0],
            endDate: todayString,
            season,
            originsKey,
          },
        }));
      }
    } catch (error) {
      console.error('❌ Error in useMarketDateSync:', error);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
    // `origins` se omite adrede: el array cambia de identidad en cada render
    // aunque su contenido sea el mismo. Se depende de `originsKey`, que es su
    // representación estable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speciesId, season, hasDemo, viewMode, demoDefaultDate, selectedDate, dateCache, daysBack, originsKey]);

  /**
   * Effect principal: se ejecuta cuando cambian speciesId o season
   * Unifica toda la lógica en un solo efecto para evitar competing
   */
  useEffect(() => {
    syncDates();
    // `originsKey` NO va acá a propósito.
    //
    // El anclaje por origen se aplica igual al montar y al cambiar especie o
    // temporada, que es lo que evita entrar a la página con el gráfico vacío.
    // Pero re-anclar cada vez que el usuario toca el filtro de origen dispara
    // un refetch, y mientras `loading` es true MarketEvolution reemplaza el
    // gráfico por un esqueleto (ver su línea del ternario `loading ? ... :`).
    // Al volver a montarse, los filtros del gráfico (calibre, variedad,
    // formato...) viven en useState con defaults y se pierde la selección.
    // Cambiar de origen mantiene el rango que el usuario ya tenía.
  }, [speciesId, season]);

  /**
   * Función para actualizar manualmente la fecha seleccionada
   * (Por ejemplo, cuando el usuario usa el date picker)
   */
  const updateSelectedDate = useCallback(
    (newDate: string) => {
      setSelectedDate(newDate);

      // Actualizar cache
      if (speciesId !== undefined) {
        const endDate = new Date(`${newDate}T12:00:00.000Z`);
        const startDate = new Date(`${newDate}T12:00:00.000Z`);
        startDate.setDate(
          startDate.getDate() - (daysBack || (viewMode === 'day' ? 7 : 15))
        );

        setDateCache((prev) => ({
          ...prev,
          [speciesId]: {
            date: newDate,
            startDate: startDate.toISOString().split('T')[0],
            endDate: newDate,
            season,
            originsKey,
          },
        }));
      }
    },
    [speciesId, season, viewMode, daysBack, originsKey]
  );

  /**
   * Función para actualizar manualmente el rango de fechas
   */
  const updateDateRange = useCallback(
    (newRange: [Date, Date]) => {
      setDateRange(newRange);

      // Actualizar también selectedDate al inicio del rango
      const startDateString = newRange[0].toISOString().split('T')[0];
      setSelectedDate(startDateString);

      // Actualizar cache
      if (speciesId !== undefined) {
        setDateCache((prev) => ({
          ...prev,
          [speciesId]: {
            date: startDateString,
            startDate: startDateString,
            endDate: newRange[1].toISOString().split('T')[0],
            season,
            originsKey,
          },
        }));
      }
    },
    [speciesId, season, originsKey]
  );

  return {
    selectedDate,
    dateRange,
    isLoading,
    updateSelectedDate,
    updateDateRange,
    // Exponer setters directos para casos específicos
    setSelectedDate,
    setDateRange,
  };
};

export default useMarketDateSync;
